import _ from "lodash";
/** External */
import React, { useEffect } from "react";
import { ToggleSwitch, Tooltip } from "@toddle-edu/ds-web";
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";

/** Internal */
import withLoader from "UIComponents/GqlWithLoader";
import Accordian from "UIComponents/Accordian";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import {
  getLibraryGradesQueryVariables,
  getSubTextFromCurriculumProgramType,
  getGradesWithDpEnabled,
} from "InternalTools/modules/InternalToolUtils";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import ConditionalWrapper from "Utils/ConditionalWrapper";

/** Relative */
import styles from "./GradeListStyles";
import classes from "./GradeList.scss";

const GradeList = ({ data, grades, onUpdateGrade, setGrades }) => {
  useEffect(() => {
    const gradesWithDpEnabled = getGradesWithDpEnabled(data);
    setGrades(gradesWithDpEnabled);
  }, [data, setGrades]);

  const headingClasses = classNames("text-label-2", classes.heading);

  const gradesGroupedByAcronym = _.groupBy(grades, "acronym");

  return (
    <div className={classes.container}>
      <div className={headingClasses}>Grades</div>
      {_.map(gradesGroupedByAcronym, (grades, acronym) => {
        return (
          <Accordian
            key={acronym}
            title={getSubTextFromCurriculumProgramType(acronym)}
            headerStyle={styles.headerStyle}
            hideContainerBorder
            showLabelBorder
            isExpanded={_.first(_.keys(gradesGroupedByAcronym)) === acronym}
            customTitleStyle={styles.titleContainer}
            expandedContent={_.map(grades, (grade, index) => {
              const { gradeUid, name, isLibraryPremium, curriculumUid } = grade;
              const customStyles =
                index === 0
                  ? styles.firstGradeItem
                  : index === _.size(grades) - 1
                  ? styles.lastGradeItem
                  : null;

              return (
                <div
                  key={gradeUid}
                  className={classes.grade}
                  style={customStyles}
                >
                  <div>{name}</div>
                  <ConditionalWrapper
                    condition={curriculumUid === "IB_DP"}
                    wrapper={
                      <Tooltip tooltip="Enabling a DP curriculum makes all the DP grades premium" />
                    }
                    type="wrapperAsElement"
                  >
                    <ToggleSwitch
                      onChange={() => onUpdateGrade(gradeUid)}
                      disabled={curriculumUid === "IB_DP"}
                      checked={isLibraryPremium}
                    />
                  </ConditionalWrapper>
                </div>
              );
            })}
          />
        );
      })}
    </div>
  );
};

export default compose(
  graphql(getInternalToolGenericDataQuery, {
    name: "getLibraryGrades",
    options: ({ organizationId, curriculumProgramIds }) => ({
      variables: getLibraryGradesQueryVariables({
        organizationId,
        curriculumProgramIds,
      }),
      fetchPolicy: "network-only",
    }),
    props: ({ getLibraryGrades }) => {
      const grades = _.get(
        getLibraryGrades,
        "platform.internalToolsGenericQuery.data"
      );

      return {
        isData: !_.isEmpty(grades),
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          getLibraryGrades.networkStatus
        ),
        data: grades,
      };
    },
  }),
  withLoader
)(GradeList);
